import React, { useState, useEffect } from 'react';
import { Plus, Layout, Settings, LogOut, Search, MoreVertical, Trash2, ExternalLink, BarChart3, Eye, Folder, Shield } from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, RadialBarChart, RadialBar, PolarAngleAxis, PolarRadiusAxis, PolarGrid, Label as RechartsLabel } from 'recharts';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardFooter, CardHeader, CardTitle, CardDescription } from './ui/card';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "./ui/dialog";
import { Label } from "./ui/label";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { toast } from 'sonner';
import ScreenPreview from './ScreenPreview';
import PricingPage from './PricingPage';
import { CreditCard, Smartphone } from 'lucide-react';
import { useSubscription } from '../contexts/SubscriptionContext';
import { useProjects } from '../contexts/ProjectsContext';
import { useTemplates } from '../contexts/TemplatesContext';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Skeleton } from "./ui/skeleton";
import AdminDashboard from './AdminDashboard';
import QRCode from 'react-qr-code';
import AnnouncementBanner from './AnnouncementBanner';
import { generateScreens } from '../services/aiService';
import { triggerEmail } from '../services/emailService';
import { trackFlowCreated, trackScreenAdded, trackPreviewOpened, trackFlowPublished, trackAIScreenGenerated, trackSDKInstalled, trackOnboardingStart } from '../services/analyticsService';
import { Sparkles, Wand2, Loader2, ChevronRight, ChevronLeft, Type, Palette, FileText, Check, LayoutTemplate, Image as ImageIcon } from 'lucide-react';
import WalkthroughModal from './WalkthroughModal';
import IntegrationChecklist from './IntegrationChecklist';
import confetti from 'canvas-confetti';

// Usage Radial Shape Component (Shadcn Style)
// Usage Radial Shape Component (Shadcn Style)
// Usage Radial Shape Component (Dark/Blue Style)
const UsageRadialShape = ({ title, current, limit, color }) => {
    const isUnlimited = limit === Infinity;
    // Calculate percentage for the chart
    // If unlimited, we show full circle? Or maybe just 75%? Let's show full.
    // Actually for unlimited projects, maybe just show a full subtle ring.
    // Current logic: if unlimited, max=100, val=100.
    const maxVal = isUnlimited ? 100 : limit;
    const value = isUnlimited ? 100 : current;

    // Data for Recharts
    const chartData = [
        {
            name: title,
            visitors: value,
            fill: color || "hsl(var(--primary))",
        },
    ];

    return (
        <Card className="flex flex-col shadow-sm border bg-card text-card-foreground relative overflow-hidden">
            <CardHeader className="items-center pb-0 z-10">
                <CardTitle className="font-semibold tracking-tight">{title}</CardTitle>
                <CardDescription>Current Usage</CardDescription>
            </CardHeader>
            <CardContent className="flex-1 pb-4 pt-4 z-10 relative">
                <div className="mx-auto aspect-square max-h-[220px] w-full relative">
                    <ResponsiveContainer width="100%" height="100%">
                        <RadialBarChart
                            data={chartData}
                            startAngle={200}
                            endAngle={-20}
                            innerRadius={80}
                            outerRadius={110}
                            barSize={20}
                        >
                            <PolarAngleAxis
                                type="number"
                                domain={[0, maxVal]}
                                tick={false}
                            />
                            <RadialBar
                                background={{ fill: 'hsl(var(--muted))' }}
                                dataKey="visitors"
                                cornerRadius={10}
                            />
                        </RadialBarChart>
                    </ResponsiveContainer>

                    {/* Centered Text Overlay */}
                    <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none pb-2">
                        <span className="text-4xl font-bold tracking-tighter shadow-sm">
                            {current.toLocaleString()}
                        </span>
                        <span className="text-sm text-muted-foreground font-medium uppercase tracking-wider mt-1">
                            {title === 'Flows' ? (current === 1 ? 'Flow' : 'Flows') : 'Users'}
                        </span>
                        {!isUnlimited && (
                            <span className="text-xs text-muted-foreground mt-1">
                                / {limit.toLocaleString()}
                            </span>
                        )}
                        {isUnlimited && (
                            <span className="text-xs text-muted-foreground mt-1">
                                Unlimited
                            </span>
                        )}
                    </div>
                </div>
            </CardContent>
        </Card>
    );
};

const Dashboard = ({ user, supabase, onSelectProject, onLogout }) => {
    const isAdmin = user.email === 'jagib07@gmail.com';
    const { subscription, loading: subscriptionLoading, canCreateProject, getPlanLimits, getCurrentMonthViews, refresh, canUseAIGeneration, trackAIUsage, verifySubscription } = useSubscription();
    const {
        projects,
        loading,
        page,
        hasMore,
        fetchProjects,
        addProject,
        removeProject
    } = useProjects();

    const {
        templates,
        searchQuery: templateSearch,
        setSearchQuery: setTemplateSearch,
        filter: templateFilter,
        setFilter: setTemplateFilter,
        getFilteredTemplates
    } = useTemplates();

    const [activeView, setActiveView] = useState('projects'); // 'projects', 'settings', or 'admin'
    const [searchQuery, setSearchQuery] = useState('');
    const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
    const [newProjectName, setNewProjectName] = useState('');
    const [isCreating, setIsCreating] = useState(false);
    const [selectedTemplate, setSelectedTemplate] = useState('blank');
    const [showPricing, setShowPricing] = useState(false);
    const [monthlyViews, setMonthlyViews] = useState(0);
    const [viewsLoading, setViewsLoading] = useState(true);
    const [paywallReason, setPaywallReason] = useState(null);

    // AI Generation State
    const [aiPrompt, setAiPrompt] = useState('');
    const [aiScreenCount, setAiScreenCount] = useState(3);
    const [aiBrandColor, setAiBrandColor] = useState('#000000');
    const [isGenerating, setIsGenerating] = useState(false);
    const [generationProgress, setGenerationProgress] = useState(null);
    const [aiStep, setAiStep] = useState(0);
    const [aiFont, setAiFont] = useState('Inter');
    const [isAIWizardOpen, setIsAIWizardOpen] = useState(false);
    const [aiReferenceImage, setAiReferenceImage] = useState(null);
    const [aiReferenceImages, setAiReferenceImages] = useState([]);
    const [isCheckingProjectLimit, setIsCheckingProjectLimit] = useState(false);




    const handleNewProjectClick = async () => {
        if (isCheckingProjectLimit) return; // Prevent double-click
        setIsCheckingProjectLimit(true);
        try {
            const allowed = await canCreateProject();
            if (allowed) {
                setNewProjectName('');
                setSelectedTemplate('blank');
                setIsCreateDialogOpen(true);
            } else {
                const limits = getPlanLimits();
                setPaywallReason({ reason: 'projects', limit: limits.projects });
                setShowPricing(true);
            }
        } finally {
            setIsCheckingProjectLimit(false);
        }
    };




    useEffect(() => {
        fetchProjects(0);
        fetchMonthlyViews();
        fetchIntegrationProgress();
        document.title = 'My Apps - Pop Onboarding';

        // Check for first-time walkthrough
        const hasCompletedWalkthrough = localStorage.getItem('onboarding_walkthrough_completed');
        if (!hasCompletedWalkthrough && projects.length === 0 && !loading) {
            // Only show if no projects (new user) and haven't seen it
            // We wait for loading to finish to confirm project count, 
            // but projects might be empty initially. 
            // Better to just check localStorage for now to avoid flashing.
            // Actually, let's wait for projects to load.
        }

        // Check for Stripe session_id
        const params = new URLSearchParams(window.location.search);
        const sessionId = params.get('session_id');
        const checkoutId = params.get('checkout_id'); // Polar checkout

        if (sessionId || checkoutId) {
            toast.promise(
                async () => {
                    // Wait a bit for Polar to process
                    await new Promise(resolve => setTimeout(resolve, 2000));
                    // Verify subscription directly with Polar API
                    const verified = await verifySubscription();
                    if (!verified.verified) {
                        // If verification failed, wait longer and try again
                        await new Promise(resolve => setTimeout(resolve, 3000));
                        await verifySubscription();
                    }
                    // Clean URL - remove all checkout-related params
                    window.history.replaceState({}, document.title, window.location.pathname);
                },
                {
                    loading: 'Verifying subscription with payment provider...',
                    success: 'Subscription verified!',
                    error: 'Could not verify subscription yet. Please refresh the page.'
                }
            );
        }
    }, []);

    // Effect to trigger walkthrough after projects load
    useEffect(() => {
        if (!loading) {
            const hasCompletedWalkthrough = localStorage.getItem('onboarding_walkthrough_completed');
            if (!hasCompletedWalkthrough && projects.length === 0) {
                setShowWalkthrough(true);
            }
            // Re-check progress when projects change
            fetchIntegrationProgress();
        }
    }, [loading, projects.length]);

    const [previewProject, setPreviewProject] = useState(null);
    const [showWalkthrough, setShowWalkthrough] = useState(false);
    const [integrationProgress, setIntegrationProgress] = useState({
        flowCreated: false,
        screenAdded: false,
        previewOpened: false,
        sdkInstalled: false,
        onboardingStarted: false,
        flowPublished: false
    });

    // Track previous progress to trigger toasts
    const prevProgressRef = React.useRef(null);
    const isFirstSync = React.useRef(true);

    const fetchIntegrationProgress = async () => {

        if (!user) return;

        // 1. Check Projects (Flow Created, Screen Added, Published)
        const flowCreated = projects.length > 0;
        const screenAdded = projects.some(p => p.data && p.data.length > 0) || localStorage.getItem('has_added_screen') === 'true';

        // 2. Check Analytics Events & Local Storage
        try {
            // Optimized: Check events individually with limit(1) to avoid loading massive table
            const checkEvent = async (type) => {
                const { data } = await supabase
                    .from('analytics_events')
                    .select('id')
                    .eq('event_type', type)
                    .limit(1);
                return data && data.length > 0;
            };

            const [hasSdk, hasOnboarding, hasPreview] = await Promise.all([
                checkEvent('sdk_installed'),
                checkEvent('onboarding_start'),
                checkEvent('preview_opened')
            ]);

            const sdkInstalled = hasSdk;
            const onboardingStarted = hasOnboarding;
            const previewOpened = hasPreview || localStorage.getItem('has_previewed_on_device') === 'true';
            const flowPublished = projects.some(p => p.published_at);

            // Trigger 'sdk_installed' email if detected and not sent yet
            if (sdkInstalled && !localStorage.getItem('email_sent_sdk_installed')) {
                triggerEmail('sdk_installed', user.email);
                trackSDKInstalled(projects[0]?.id);
                localStorage.setItem('email_sent_sdk_installed', 'true');
            }

            // Track onboarding start if detected
            if (onboardingStarted && !localStorage.getItem('tracked_onboarding_start')) {
                trackOnboardingStart(projects[0]?.id);
                localStorage.setItem('tracked_onboarding_start', 'true');
            }

            const newProgress = {
                flowCreated,
                screenAdded,
                previewOpened,
                sdkInstalled,
                onboardingStarted,
                flowPublished
            };

            setIntegrationProgress(newProgress);

            // Skip toasts on first sync
            if (isFirstSync.current) {
                isFirstSync.current = false;
                prevProgressRef.current = newProgress;
                return;
            }

            // Check for new achievements and toast
            if (prevProgressRef.current) {
                const prev = prevProgressRef.current;

                const triggerConfetti = () => {
                    confetti({
                        particleCount: 100,
                        spread: 70,
                        origin: { x: 1, y: 0.1 },
                        colors: ['#26ccff', '#a25afd', '#ff5e7e', '#88ff5a', '#fcff42', '#ffa62d', '#ff36ff']
                    });
                };

                const showSuccess = (msg, id) => {
                    const storageKey = `toast_${id}`;
                    if (!localStorage.getItem(storageKey)) {
                        toast.success(msg, { id });
                        triggerConfetti();
                        localStorage.setItem(storageKey, 'true');
                    }
                };

                if (!prev.flowCreated && newProgress.flowCreated) showSuccess('Flow created! 🚀', 'flow-created');
                if (!prev.screenAdded && newProgress.screenAdded) showSuccess('First screen added! 🎨', 'screen-added');
                if (!prev.previewOpened && newProgress.previewOpened) showSuccess('Previewed on device! 📱', 'preview-opened');
                if (!prev.sdkInstalled && newProgress.sdkInstalled) showSuccess('SDK installed successfully! 🛠️', 'sdk-installed');
                if (!prev.onboardingStarted && newProgress.onboardingStarted) showSuccess('First onboarding session detected! ⚡', 'onboarding-started');
                if (!prev.flowPublished && newProgress.flowPublished) showSuccess('Flow published live! 🌍', 'flow-published');
            }

            // Update ref
            prevProgressRef.current = newProgress;

        } catch (err) {
            console.error('Error fetching integration progress:', err);
        }
    };

    const handleWalkthroughComplete = () => {
        localStorage.setItem('onboarding_walkthrough_completed', 'true');
        setShowWalkthrough(false);
    };

    const handleWalkthroughAction = (stepId) => {
        if (stepId === 'create_flow') {
            handleNewProjectClick();
            // We don't close the walkthrough, it stays open or we can close it?
            // If we open the create dialog, the walkthrough might be behind it.
            // Let's close it for now so they can focus on creation.
            // Or maybe keep it open? Dialogs stack.
            // Let's keep it open but maybe minimize or just let it stack.
            // Actually, if they create a flow, they go to Editor.
            // So we should probably close it if they click "Create Flow" AND successfully create one?
            // For now, let's just open the dialog.
        }
    };

    const fetchMonthlyViews = async () => {
        try {
            setViewsLoading(true);
            const views = await getCurrentMonthViews();
            setMonthlyViews(views);
        } catch (error) {
            console.error('Error fetching monthly views:', error);
        } finally {
            setViewsLoading(false);
        }
    };

    const handleLoadMore = () => {
        fetchProjects(page + 1);
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        const now = new Date();
        const isToday = date.toDateString() === now.toDateString();
        const isYesterday = new Date(now - 86400000).toDateString() === date.toDateString();

        if (isToday) {
            return `Today at ${date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`;
        } else if (isYesterday) {
            return `Yesterday at ${date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`;
        } else {
            return date.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
        }
    };

    const handleCreateProject = async (e) => {
        e?.preventDefault(); // Prevent form submission if wrapped in form

        if (!newProjectName.trim()) {
            toast.error('Please enter a project name');
            return;
        }

        try {
            setIsCreating(true);
            let projectData = [];
            let settings = {
                fontFamily: 'Inter',
                backgroundColor: '#ffffff'
            };
            let aiGenerationUsed = false;

            if (selectedTemplate === 'ai-generated') {
                // Check AI plan access
                const plan = subscription?.plan_type || 'free';

                // Check AI usage limits
                const aiCheck = await canUseAIGeneration();
                if (!aiCheck.allowed) {
                    if (aiCheck.reason === 'monthly_limit') {
                        setPaywallReason({
                            reason: 'ai_limit',
                            message: `You've used ${aiCheck.current}/${aiCheck.limit} AI generations this month. Upgrade for more!`,
                            limit: aiCheck.limit,
                            current: aiCheck.current
                        });
                        setShowPricing(true);
                    } else if (aiCheck.reason === 'rate_limit') {
                        toast.error(aiCheck.message || 'Too many requests. Please wait an hour.');
                    } else {
                        toast.error('Unable to use AI generation. Please try again.');
                    }
                    setIsCreating(false);
                    return;
                }

                if (!aiPrompt.trim()) {
                    toast.error('Please enter a prompt for AI generation');
                    setIsCreating(false);
                    return;
                }

                try {
                    setIsGenerating(true);
                    setGenerationProgress({ step: 1, message: 'Initializing AI...' });

                    // Simulate progress steps
                    setTimeout(() => setGenerationProgress({ step: 2, message: 'Creating welcome screen...' }), 500);
                    setTimeout(() => setGenerationProgress({ step: 3, message: 'Adding welcome elements...' }), 1500);
                    setTimeout(() => setGenerationProgress({ step: 4, message: 'Designing feature screens...' }), 3000);
                    setTimeout(() => setGenerationProgress({ step: 5, message: 'Adding buttons and interactions...' }), 4500);
                    setTimeout(() => setGenerationProgress({ step: 6, message: 'Finalizing your onboarding flow...' }), 6000);

                    // Pass supabase client instead of apiKey
                    const generatedScreens = await generateScreens(supabase, aiPrompt, aiScreenCount, {
                        primaryColor: aiBrandColor,
                        backgroundColor: '#ffffff',
                        fontFamily: aiFont
                    }, aiReferenceImages);
                    projectData = generatedScreens;
                    settings.backgroundColor = '#ffffff';
                    settings.fontFamily = aiFont;
                    setGenerationProgress({ step: 7, message: 'Done! ✨' });
                    setIsGenerating(false);

                    // Track AI usage (will be linked to project after creation)
                    aiGenerationUsed = true;
                } catch (err) {
                    console.error("AI Generation failed:", err);
                    toast.error(err.message || 'AI Generation failed. Please try again.');
                    setIsCreating(false);
                    setIsGenerating(false);
                    setGenerationProgress(null);
                    return;
                }
            } else {
                // Get the selected template from context
                const template = templates.find(t => t.id === selectedTemplate) || templates[0];
                projectData = template.screens;
            }

            const newProject = {
                user_id: user.id,
                name: newProjectName,
                data: projectData,
                variants: [],
                ab_test_config: {
                    enabled: false,
                    traffic_split: [0.5, 0.5]
                },
                settings: settings
            };

            const { data, error } = await supabase
                .from('projects')
                .insert(newProject)
                .select()
                .single();

            if (error) throw error;

            addProject(data);
            onSelectProject(data);

            // Track AI usage if it was used
            if (aiGenerationUsed) {
                await trackAIUsage('generation', data.id);
            }

            // Trigger 'flow_created' email if this is the first project
            // Use localStorage to ensure this only happens once ever
            if (projects.length === 0 && !localStorage.getItem('email_sent_flow_created')) {
                triggerEmail('flow_created', user.email, { projectName: data.name });
                localStorage.setItem('email_sent_flow_created', 'true');
            }

            // Track analytics
            const method = selectedTemplate === 'ai-generated' ? 'ai' : selectedTemplate === 'blank' ? 'blank' : 'template';
            trackFlowCreated(data.id, data.name, method);
            if (aiGenerationUsed) {
                trackAIScreenGenerated(data.id, aiPrompt, projectData.length);
            }

            toast.success('Project created!');
            setIsCreateDialogOpen(false);
            setIsAIWizardOpen(false);
            setNewProjectName('');
            setSelectedTemplate('blank');
            setAiPrompt('');
        } catch (error) {
            console.error('Error creating project:', error);
            toast.error('Failed to create project');
        } finally {
            setIsCreating(false);
            setIsGenerating(false);
            setGenerationProgress(null);
        }
    };

    const handleDeleteProject = async (projectId, e) => {
        e.stopPropagation();
        if (!window.confirm('Are you sure you want to delete this project?')) return;

        try {
            const { error } = await supabase
                .from('projects')
                .delete()
                .eq('id', projectId);

            if (error) throw error;

            removeProject(projectId);
            toast.success('Project deleted');
        } catch (error) {
            console.error('Error deleting project:', error);
            toast.error('Failed to delete project');
        }
    };

    const filteredProjects = projects.filter(p =>
        p.name.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const filteredTemplates = getFilteredTemplates(user.id);

    return (
        <div className="flex h-screen bg-background">
            {/* Sidebar */}
            <div className="w-64 border-r border-border bg-card flex flex-col">
                <div className="p-6 border-b border-border">
                    <div className="flex items-center gap-3">
                        <img src="/logo.png" alt="Pop Onboarding" className="h-8 w-8" />
                        <div className="flex flex-col">
                            <span className="font-bold text-xl leading-none">Pop Onboarding</span>
                            {process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY?.startsWith('pk_test_') && (
                                <span className="text-[10px] font-mono bg-yellow-100 text-yellow-800 px-1 rounded w-fit mt-1">
                                    TEST MODE
                                </span>
                            )}
                        </div>
                    </div>
                </div>

                <div className="flex-1 p-4 space-y-2">
                    <Button
                        variant={activeView === 'projects' ? "secondary" : "ghost"}
                        className="w-full justify-start"
                        onClick={() => setActiveView('projects')}
                    >
                        <Layout className="mr-2 h-4 w-4" />
                        All Apps
                    </Button>
                    <Button
                        variant={activeView === 'settings' ? "secondary" : "ghost"}
                        className="w-full justify-start"
                        onClick={() => setActiveView('settings')}
                    >
                        <Settings className="mr-2 h-4 w-4" />
                        Settings
                    </Button>
                    {isAdmin && (
                        <Button
                            variant={activeView === 'admin' ? "secondary" : "ghost"}
                            className="w-full justify-start"
                            onClick={() => setActiveView('admin')}
                        >
                            <Shield className="mr-2 h-4 w-4" />
                            Admin Dashboard
                        </Button>
                    )}
                    <Button variant="ghost" className="w-full justify-start text-primary hover:text-primary hover:bg-primary/10" onClick={() => setShowPricing(true)}>
                        <CreditCard className="mr-2 h-4 w-4" />
                        Upgrade Plan
                    </Button>
                </div>

                {/* Usage Stats Removed from Sidebar */}

                <div className="px-4 py-2">
                    <IntegrationChecklist integrationProgress={integrationProgress} />
                </div>

                <div className="p-4 border-t border-border">
                    <div className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted/50 cursor-pointer">
                        <Avatar>
                            <AvatarImage src={user.user_metadata?.avatar_url} />
                            <AvatarFallback>{user.email?.charAt(0).toUpperCase()}</AvatarFallback>
                        </Avatar>
                        <div className="flex-1 overflow-hidden">
                            <p className="text-sm font-medium truncate">{user.email}</p>
                            <div className="flex items-center mt-1">
                                {subscriptionLoading ? (
                                    <span className="flex items-center text-[10px] px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500 font-medium uppercase">
                                        <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                                        Loading subscription...
                                    </span>
                                ) : (
                                    <span
                                        className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium uppercase ${subscription?.plan_type === 'growth' ? 'bg-purple-100 text-purple-700' :
                                            subscription?.plan_type === 'starter' ? 'bg-blue-100 text-blue-700' :
                                                subscription?.plan_type === 'scale' ? 'text-yellow-900 border border-yellow-200' :
                                                    'bg-gray-100 text-gray-600'
                                            }`}
                                        style={subscription?.plan_type === 'scale' ? { backgroundColor: '#fff1b0' } : {}}
                                    >
                                        {subscription?.plan_type || 'Free'} Plan
                                    </span>
                                )}
                            </div>
                        </div>
                        <Button variant="ghost" size="icon" onClick={onLogout}>
                            <LogOut className="h-4 w-4" />
                        </Button>
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <div className="flex-1 flex flex-col overflow-hidden">
                {activeView === 'projects' ? (
                    <>
                        <header className="h-16 border-b border-border flex items-center justify-between px-8 bg-card/50 backdrop-blur">
                            <h1 className="text-2xl font-bold">My Apps</h1>
                            <div className="flex items-center gap-4">
                                <div className="relative w-64">
                                    <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                                    <Input
                                        placeholder="Search apps..."
                                        className="pl-8"
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                    />
                                </div>
                                <div className="flex gap-2">
                                    {/* AI Generation button hidden - code preserved for future reactivation
                                    <Button
                                        onClick={() => {
                                            setIsAIWizardOpen(true);
                                            setAiStep(0);
                                            setAiPrompt('');
                                            setNewProjectName('');
                                            setAiReferenceImage(null);
                                            setAiReferenceImages([]);
                                            setSelectedTemplate('ai-generated');
                                        }}
                                        className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white border-0"
                                    >
                                        <Sparkles className="mr-2 h-4 w-4" /> Generate with AI
                                    </Button>
                                    */}
                                    <Button onClick={handleNewProjectClick} disabled={isCheckingProjectLimit}>
                                        {isCheckingProjectLimit ? (
                                            <><span className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" /> Verifying...</>
                                        ) : (
                                            <><Plus className="mr-2 h-4 w-4" /> New App</>
                                        )}
                                    </Button>
                                </div>
                                <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
                                    <DialogContent className="sm:max-w-[800px] max-h-[80vh] flex flex-col">
                                        <DialogHeader>
                                            <DialogTitle>Create New App</DialogTitle>
                                            <DialogDescription>
                                                Choose a template or start from scratch.
                                            </DialogDescription>
                                        </DialogHeader>

                                        <div className="flex-1 overflow-y-auto py-4 pr-2">
                                            <div className="grid gap-6">
                                                <div className="grid grid-cols-4 items-center gap-4">
                                                    <Label htmlFor="name" className="text-right">
                                                        App Name
                                                    </Label>
                                                    <Input
                                                        id="name"
                                                        value={newProjectName}
                                                        onChange={(e) => setNewProjectName(e.target.value)}
                                                        placeholder="My Awesome App"
                                                        className="col-span-3"
                                                        autoFocus
                                                    />
                                                </div>

                                                <div className="space-y-4">
                                                    <div className="flex items-center justify-between">
                                                        <Label>Choose a Template</Label>
                                                        <div className="relative w-64">
                                                            <Search className="absolute left-2 top-2.5 h-3 w-3 text-muted-foreground" />
                                                            <Input
                                                                placeholder="Search templates..."
                                                                className="pl-8 h-8 text-sm"
                                                                value={templateSearch}
                                                                onChange={(e) => setTemplateSearch(e.target.value)}
                                                            />
                                                        </div>
                                                    </div>

                                                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                                                        {filteredTemplates.map((template) => (
                                                            <div
                                                                key={template.id}
                                                                onClick={() => setSelectedTemplate(template.id)}
                                                                className={`p-4 border-2 rounded-lg cursor-pointer transition-all hover:border-primary/50 relative group ${selectedTemplate === template.id
                                                                    ? 'border-primary bg-primary/5'
                                                                    : 'border-border'
                                                                    }`}
                                                            >
                                                                <div className="aspect-video bg-muted rounded mb-2 flex items-center justify-center text-4xl overflow-hidden">
                                                                    {template.thumbnail_url ? (
                                                                        <img src={template.thumbnail_url} alt={template.name} className="w-full h-full object-cover" />
                                                                    ) : (
                                                                        template.id === 'pig-app' ? '🐷' : template.id === 'saas-welcome' ? '🚀' : template.id === 'paywall' ? '💰' : template.id === 'email-capture' ? '📧' : '📄'
                                                                    )}
                                                                </div>
                                                                <h4 className="font-semibold text-sm truncate">{template.name}</h4>
                                                                <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{template.description}</p>

                                                                {template.is_official && (
                                                                    <span className="absolute top-2 right-2 bg-blue-100 text-blue-700 text-[10px] px-1.5 py-0.5 rounded-full font-medium">
                                                                        Official
                                                                    </span>
                                                                )}
                                                            </div>
                                                        ))}
                                                    </div>

                                                    {filteredTemplates.length === 0 && (
                                                        <div className="text-center py-8 text-muted-foreground text-sm">
                                                            No templates found matching your search.
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>

                                        <DialogFooter className="mt-4">
                                            <Button type="submit" onClick={handleCreateProject} disabled={isCreating}>
                                                {isCreating ? 'Creating...' : 'Create App'}
                                            </Button>
                                        </DialogFooter>
                                    </DialogContent>
                                </Dialog>

                                <Dialog open={isAIWizardOpen} onOpenChange={setIsAIWizardOpen}>
                                    <DialogContent className="sm:max-w-[800px] h-[80vh] flex flex-col p-0 overflow-hidden">
                                        <div className="p-6 border-b flex items-center justify-between">
                                            <div>
                                                <DialogTitle className="flex items-center gap-2 text-xl">
                                                    <Sparkles className="h-5 w-5" />
                                                    AI App Generator
                                                </DialogTitle>
                                                <DialogDescription className="mt-1">
                                                    Create a complete onboarding flow in seconds.
                                                </DialogDescription>
                                            </div>
                                        </div>

                                        <div className="flex-1 overflow-hidden relative p-6">
                                            {generationProgress ? (
                                                <div className="flex flex-col items-center justify-center h-full space-y-6 animate-in fade-in duration-500">
                                                    <div className="relative">
                                                        <div className="absolute inset-0 bg-purple-500/20 blur-xl rounded-full"></div>
                                                        <Loader2 className="h-12 w-12 text-purple-600 animate-spin relative z-10" />
                                                    </div>
                                                    <div className="space-y-2 text-center max-w-xs mx-auto">
                                                        <h3 className="text-lg font-semibold text-foreground animate-pulse">
                                                            {generationProgress.message}
                                                        </h3>
                                                        <div className="w-full bg-secondary h-2 rounded-full overflow-hidden">
                                                            <div
                                                                className="bg-purple-600 h-full transition-all duration-500 ease-out"
                                                                style={{ width: `${(generationProgress.step / 7) * 100}%` }}
                                                            />
                                                        </div>
                                                        <p className="text-xs text-muted-foreground">
                                                            Step {generationProgress.step} of 7
                                                        </p>
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className="h-full flex flex-col">
                                                    {/* Wizard Steps Indicator */}
                                                    <div className="flex justify-between mb-8 px-8 relative">
                                                        <div className="absolute top-1/2 left-0 w-full h-0.5 bg-muted -z-10 transform -translate-y-1/2"></div>
                                                        {[0, 1, 2].map((step) => (
                                                            <div
                                                                key={step}
                                                                className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-colors duration-300 ${aiStep >= step ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
                                                                    }`}
                                                            >
                                                                {aiStep > step ? <Check className="h-4 w-4" /> : step + 1}
                                                            </div>
                                                        ))}
                                                    </div>

                                                    {/* Step Content */}
                                                    <div className="flex-1 overflow-y-auto px-1 space-y-4">
                                                        {aiStep === 0 && (
                                                            <div className="space-y-4 animate-in fade-in slide-in-from-right-4 duration-300">
                                                                <div className="space-y-2">
                                                                    <Label className="text-base font-semibold flex items-center gap-2">
                                                                        <LayoutTemplate className="h-4 w-4" />
                                                                        What should we call your app?
                                                                    </Label>
                                                                    <Input
                                                                        value={newProjectName}
                                                                        onChange={(e) => setNewProjectName(e.target.value)}
                                                                        placeholder="e.g. FitTrack Pro"
                                                                        className="text-lg"
                                                                        autoFocus
                                                                    />
                                                                </div>
                                                                <div className="space-y-2">
                                                                    <Label className="text-base font-semibold flex items-center gap-2">
                                                                        <FileText className="h-4 w-4" />
                                                                        Describe your app's purpose
                                                                    </Label>
                                                                    <textarea
                                                                        className="flex min-h-[120px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 resize-none"
                                                                        placeholder="e.g. A meditation app helping users reduce stress with daily guided sessions and progress tracking..."
                                                                        value={aiPrompt}
                                                                        onChange={(e) => {
                                                                            setAiPrompt(e.target.value);
                                                                            setSelectedTemplate('ai-generated');
                                                                        }}
                                                                    />
                                                                </div>
                                                                <div className="space-y-2">
                                                                    <Label className="text-base font-semibold flex items-center gap-2">
                                                                        <ImageIcon className="h-4 w-4" />
                                                                        Reference Image (Optional)
                                                                    </Label>
                                                                    <Input
                                                                        type="file"
                                                                        accept="image/*"
                                                                        multiple
                                                                        onChange={(e) => {
                                                                            const files = Array.from(e.target.files);
                                                                            if (files.length > 0) {
                                                                                setAiReferenceImages(files.slice(0, 3)); // Max 3 images
                                                                            }
                                                                        }}
                                                                    />
                                                                    {aiReferenceImages.length > 0 && (
                                                                        <div className="text-xs text-muted-foreground space-y-1">
                                                                            {aiReferenceImages.map((file, idx) => (
                                                                                <div key={idx} className="flex items-center gap-2">
                                                                                    <span>• {file.name}</span>
                                                                                </div>
                                                                            ))}
                                                                            <p className="text-xs text-muted-foreground mt-1">Max 3 images</p>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        )}

                                                        {aiStep === 1 && (
                                                            <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-300">
                                                                <div className="space-y-3">
                                                                    <Label className="text-base font-semibold flex items-center gap-2">
                                                                        <Palette className="h-4 w-4" />
                                                                        Choose your brand color
                                                                    </Label>
                                                                    <div className="flex gap-4 items-center p-4 border rounded-lg bg-card">
                                                                        <Input
                                                                            type="color"
                                                                            className="w-16 h-16 p-1 rounded-full cursor-pointer"
                                                                            value={aiBrandColor}
                                                                            onChange={(e) => setAiBrandColor(e.target.value)}
                                                                        />
                                                                        <div className="flex-1">
                                                                            <Input
                                                                                value={aiBrandColor}
                                                                                onChange={(e) => setAiBrandColor(e.target.value)}
                                                                                className="font-mono uppercase"
                                                                            />
                                                                            <p className="text-xs text-muted-foreground mt-1">
                                                                                This will be used for buttons and highlights.
                                                                            </p>
                                                                        </div>
                                                                    </div>
                                                                </div>

                                                                <div className="space-y-3">
                                                                    <Label className="text-base font-semibold flex items-center gap-2">
                                                                        <Type className="h-4 w-4" />
                                                                        Select a font family
                                                                    </Label>
                                                                    <Select value={aiFont} onValueChange={setAiFont}>
                                                                        <SelectTrigger className="w-full">
                                                                            <SelectValue placeholder="Select a font" />
                                                                        </SelectTrigger>
                                                                        <SelectContent>
                                                                            <SelectItem value="Inter">Inter (Modern & Clean)</SelectItem>
                                                                            <SelectItem value="Roboto">Roboto (Standard)</SelectItem>
                                                                            <SelectItem value="Open Sans">Open Sans (Friendly)</SelectItem>
                                                                            <SelectItem value="Lato">Lato (Balanced)</SelectItem>
                                                                            <SelectItem value="Montserrat">Montserrat (Geometric)</SelectItem>
                                                                            <SelectItem value="Poppins">Poppins (Rounded)</SelectItem>
                                                                            <SelectItem value="Oswald">Oswald (Bold Condensed)</SelectItem>
                                                                            <SelectItem value="Raleway">Raleway (Elegant)</SelectItem>
                                                                            <SelectItem value="Merriweather">Merriweather (Serif)</SelectItem>
                                                                            <SelectItem value="Playfair Display">Playfair Display (Classic Serif)</SelectItem>
                                                                        </SelectContent>
                                                                    </Select>
                                                                </div>
                                                            </div>
                                                        )}

                                                        {aiStep === 2 && (
                                                            <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-300">
                                                                <div className="space-y-3">
                                                                    <Label className="text-base font-semibold flex items-center gap-2">
                                                                        <LayoutTemplate className="h-4 w-4" />
                                                                        How many screens?
                                                                    </Label>
                                                                    <div className="flex items-center gap-4">
                                                                        <Input
                                                                            type="number"
                                                                            min={1}
                                                                            max={(() => {
                                                                                const plan = subscription?.plan_type || 'free';
                                                                                if (plan === 'free') return 3;
                                                                                if (plan === 'starter') return 30;
                                                                                return 50; // growth
                                                                            })()}
                                                                            value={aiScreenCount}
                                                                            onChange={(e) => setAiScreenCount(parseInt(e.target.value))}
                                                                            className="w-24 text-center text-lg"
                                                                        />
                                                                        <span className="text-muted-foreground">screens</span>
                                                                    </div>
                                                                    <p className="text-xs text-muted-foreground">
                                                                        {(() => {
                                                                            const plan = subscription?.plan_type || 'free';
                                                                            if (plan === 'free') return 'Free plan: up to 3 screens';
                                                                            if (plan === 'starter') return 'Starter plan: up to 30 screens';
                                                                            return 'Growth plan: up to 50 screens';
                                                                        })()}
                                                                    </p>
                                                                </div>

                                                                <div className="p-4 bg-muted/30 rounded-lg border space-y-3">
                                                                    <h4 className="font-medium flex items-center gap-2">
                                                                        <Sparkles className="h-4 w-4 text-purple-500" />
                                                                        Summary
                                                                    </h4>
                                                                    <div className="text-sm space-y-1 text-muted-foreground">
                                                                        <p>App: <span className="font-medium text-foreground">{newProjectName || 'Untitled App'}</span></p>
                                                                        <p>Screens: <span className="font-medium text-foreground">{aiScreenCount}</span></p>
                                                                        <p>Font: <span className="font-medium text-foreground">{aiFont}</span></p>
                                                                        <div className="flex items-center gap-2">
                                                                            Color:
                                                                            <div className="w-4 h-4 rounded-full" style={{ backgroundColor: aiBrandColor }}></div>
                                                                            <span className="font-medium text-foreground">{aiBrandColor}</span>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>

                                                    {/* Wizard Footer */}
                                                    <div className="flex justify-between mt-6 pt-4 border-t">
                                                        <Button
                                                            variant="ghost"
                                                            onClick={() => setAiStep(s => Math.max(s - 1, 0))}
                                                            disabled={aiStep === 0}
                                                        >
                                                            <ChevronLeft className="mr-2 h-4 w-4" /> Back
                                                        </Button>

                                                        {aiStep < 2 ? (
                                                            <Button
                                                                onClick={() => {
                                                                    if (aiStep === 0 && (!newProjectName.trim() || !aiPrompt.trim())) {
                                                                        toast.error('Please fill in all fields');
                                                                        return;
                                                                    }
                                                                    setAiStep(s => s + 1);
                                                                }}
                                                            >
                                                                Next <ChevronRight className="ml-2 h-4 w-4" />
                                                            </Button>
                                                        ) : (
                                                            <Button onClick={handleCreateProject}>
                                                                <Wand2 className="mr-2 h-4 w-4" /> Generate App
                                                            </Button>
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </DialogContent>
                                </Dialog>
                            </div>
                        </header>

                        <main className="flex-1 overflow-auto p-8">
                            <AnnouncementBanner supabase={supabase} />
                            {loading ? (
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
                                    {[1, 2, 3, 4, 5, 6].map((i) => (
                                        <div key={i} className="border rounded-lg overflow-hidden bg-card">
                                            <div className="p-4 pb-2">
                                                <div className="flex justify-between items-start">
                                                    <Skeleton className="h-6 w-1/2" />
                                                    <Skeleton className="h-8 w-8 rounded-md" />
                                                </div>
                                            </div>
                                            <div className="p-4 pt-0">
                                                <Skeleton className="h-40 w-full rounded-md" />
                                            </div>
                                            <div className="p-4 pt-0">
                                                <Skeleton className="h-3 w-1/3" />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : filteredProjects.length === 0 ? (
                                <div className="flex flex-col items-center justify-center py-16 px-4 text-center border-2 border-dashed rounded-xl bg-muted/10">
                                    <div className="bg-primary/10 p-4 rounded-full mb-4">
                                        <Sparkles className="h-8 w-8 text-primary" />
                                    </div>
                                    <h3 className="text-xl font-semibold mb-2">Create your first flow</h3>
                                    <p className="text-muted-foreground max-w-md mb-6">
                                        Get started by creating a new onboarding flow. You can use our AI wizard, choose a template, or start from scratch.
                                    </p>
                                    <Button onClick={handleNewProjectClick} size="lg" className="gap-2" disabled={isCheckingProjectLimit}>
                                        {isCheckingProjectLimit ? (
                                            <><span className="h-5 w-5 animate-spin rounded-full border-2 border-current border-t-transparent" /> Verifying...</>
                                        ) : (
                                            <><Plus className="h-5 w-5" /> Create New App</>
                                        )}
                                    </Button>
                                </div>
                            ) : (
                                <>
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
                                        {filteredProjects.map((project) => (
                                            <Card
                                                key={project.id}
                                                className="cursor-pointer hover:border-primary transition-colors group"
                                                onClick={() => onSelectProject(project)}
                                            >
                                                <CardHeader className="pb-2">
                                                    <div className="flex justify-between items-start">
                                                        <CardTitle className="text-lg">{project.name}</CardTitle>
                                                        <DropdownMenu>
                                                            <DropdownMenuTrigger asChild>
                                                                <Button variant="ghost" size="icon" className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity">
                                                                    <MoreVertical className="h-4 w-4" />
                                                                </Button>
                                                            </DropdownMenuTrigger>
                                                            <DropdownMenuContent align="end">
                                                                <DropdownMenuItem onClick={(e) => handleDeleteProject(project.id, e)} className="text-destructive">
                                                                    <Trash2 className="mr-2 h-4 w-4" />
                                                                    Delete
                                                                </DropdownMenuItem>
                                                            </DropdownMenuContent>
                                                        </DropdownMenu>
                                                    </div>
                                                </CardHeader>
                                                <CardContent>
                                                    <div className="aspect-video bg-muted/30 rounded-md p-4 flex items-center justify-center gap-2 overflow-hidden relative group-hover:bg-muted/50 transition-colors">
                                                        {project.data ? (
                                                            <>
                                                                {project.data.slice(0, 3).map((screen, idx) => (
                                                                    <ScreenPreview
                                                                        key={idx}
                                                                        screen={screen}
                                                                        globalSettings={project.settings}
                                                                    />
                                                                ))}
                                                                {project.data.length === 0 && (
                                                                    <div className="text-muted-foreground text-sm">Empty App</div>
                                                                )}
                                                                {project.data.length > 3 && (
                                                                    <div className="h-8 w-8 rounded-full bg-background border flex items-center justify-center text-xs font-medium absolute right-2 bottom-2 shadow-sm">
                                                                        +{project.data.length - 3}
                                                                    </div>
                                                                )}
                                                            </>
                                                        ) : (
                                                            <div className="flex flex-col items-center justify-center text-muted-foreground">
                                                                <Layout className="h-8 w-8 mb-2 opacity-50" />
                                                                <span className="text-xs">Open to view</span>
                                                            </div>
                                                        )}
                                                    </div>
                                                </CardContent>
                                                <CardFooter className="flex justify-between items-center text-xs text-muted-foreground pt-0 pb-3 px-4">
                                                    <span>Last updated: {formatDate(project.updated_at)}</span>
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        className="h-7 px-2 text-xs gap-1 hover:bg-indigo-50 hover:text-indigo-600"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setPreviewProject(project);
                                                            trackPreviewOpened(project.id, 'dashboard_card');
                                                            localStorage.setItem('has_previewed_on_device', 'true');
                                                        }}
                                                    >
                                                        <Smartphone className="h-3 w-3" />
                                                        Preview
                                                    </Button>
                                                </CardFooter>
                                            </Card>
                                        ))}
                                    </div>

                                    {/* Preview Dialog */}
                                    <Dialog open={!!previewProject} onOpenChange={(open) => !open && setPreviewProject(null)}>
                                        <DialogContent className="sm:max-w-md">
                                            <DialogHeader>
                                                <DialogTitle>Preview {previewProject?.name}</DialogTitle>
                                                <DialogDescription>
                                                    {previewProject?.published_at
                                                        ? 'Scan this QR code with the Pop Onboarding app to view the live flow.'
                                                        : 'This flow is currently a draft. Open the Editor to preview and test your changes.'}
                                                </DialogDescription>
                                            </DialogHeader>

                                            {previewProject?.published_at ? (
                                                <div className="flex flex-col items-center justify-center p-6 space-y-6">
                                                    <div className="bg-white p-4 rounded-xl shadow-sm border">
                                                        {previewProject && (
                                                            <QRCode
                                                                value={JSON.stringify({
                                                                    projectId: previewProject.id,
                                                                    apiKey: previewProject.api_key
                                                                })}
                                                                size={200}
                                                                level="M"
                                                            />
                                                        )}
                                                    </div>
                                                    <div className="text-center space-y-4">
                                                        <p className="text-xs text-muted-foreground max-w-[200px] mx-auto">
                                                            Open the Pop Onboarding app and scan this code to instantly load your flow.
                                                        </p>
                                                        <div className="flex flex-col gap-3 items-center">
                                                            <a
                                                                href="https://apps.apple.com/us/app/pop-onboarding/id6755694268"
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="text-xs text-primary hover:underline flex items-center gap-1"
                                                            >
                                                                Download for iOS <ExternalLink className="h-3 w-3" />
                                                            </a>
                                                        </div>
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className="flex flex-col items-center justify-center p-8 space-y-6">
                                                    <div className="bg-muted/30 p-6 rounded-full">
                                                        <Smartphone className="h-10 w-10 text-muted-foreground/50" />
                                                    </div>
                                                    <div className="text-center space-y-2">
                                                        <p className="text-sm font-medium">To preview edits, you must use the Editor.</p>
                                                        <p className="text-xs text-muted-foreground max-w-[260px] mx-auto">
                                                            The dashboard preview only shows published flows.
                                                        </p>
                                                    </div>
                                                    <Button
                                                        onClick={() => {
                                                            if (previewProject) {
                                                                onSelectProject(previewProject);
                                                                setPreviewProject(null);
                                                            }
                                                        }}
                                                        className="w-full"
                                                    >
                                                        Open in Editor
                                                    </Button>
                                                </div>
                                            )}
                                        </DialogContent>
                                    </Dialog>
                                    {hasMore && !searchQuery && (
                                        <div className="flex justify-center pb-8">
                                            <Button variant="outline" onClick={handleLoadMore}>
                                                Load More
                                            </Button>
                                        </div>
                                    )}


                                </>
                            )}
                        </main >
                    </>
                ) : activeView === 'admin' && isAdmin ? (
                    <AdminDashboard user={user} supabase={supabase} />
                ) : activeView === 'settings' ? (
                    <>
                        <header className="h-16 border-b border-border flex items-center justify-between px-8 bg-card/50 backdrop-blur">
                            <h1 className="text-2xl font-bold">Account Settings</h1>
                        </header>
                        <main className="flex-1 overflow-auto p-8">
                            <div className="max-w-2xl space-y-8">
                                {/* Profile Section */}
                                <section className="space-y-4">
                                    <h2 className="text-lg font-semibold">Profile</h2>
                                    <div className="flex items-center gap-4 p-4 border rounded-lg bg-card">
                                        <Avatar className="h-16 w-16">
                                            <AvatarImage src={user.user_metadata?.avatar_url} />
                                            <AvatarFallback>{user.email?.charAt(0).toUpperCase()}</AvatarFallback>
                                        </Avatar>
                                        <div>
                                            <p className="font-medium">{user.email}</p>
                                            <p className="text-sm text-muted-foreground">User ID: {user.id}</p>
                                        </div>
                                    </div>
                                </section>

                                {/* Subscription Section */}
                                <section className="space-y-4">
                                    <h2 className="text-lg font-semibold">Subscription</h2>
                                    <div className="p-4 border rounded-lg bg-card space-y-4">
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <p className="font-medium capitalize">{subscription?.plan_type || 'Free'} Plan</p>
                                                <p className="text-sm text-muted-foreground">
                                                    {subscription?.plan_type === 'growth' ? 'Unlimited everything' :
                                                        subscription?.plan_type === 'starter' ? 'Increased limits' :
                                                            'Basic limits'}
                                                </p>
                                            </div>
                                            <div className="flex gap-2">
                                                <Button variant="outline" onClick={() => setShowPricing(true)}>
                                                    Upgrade Plan
                                                </Button>
                                                <Button
                                                    variant="outline"
                                                    onClick={async () => {
                                                        try {
                                                            if (subscription?.polar_customer_id) {
                                                                const { data, error } = await supabase.functions.invoke('create-customer-portal', {
                                                                    body: {
                                                                        userId: user.id,
                                                                        customerId: subscription.polar_customer_id
                                                                    }
                                                                });

                                                                if (error) throw error;
                                                                if (data?.url) window.open(data.url, '_blank');
                                                            } else {
                                                                // Fallback if no customer ID yet
                                                                window.open('https://polar.sh/pop-onboarding/portal', '_blank');
                                                            }
                                                        } catch (err) {
                                                            console.error('Error opening portal:', err);
                                                            window.open('https://polar.sh/pop-onboarding/portal', '_blank');
                                                        }
                                                    }}
                                                >
                                                    Billing Portal
                                                </Button>
                                            </div>
                                        </div>
                                        <div className="space-y-2 pt-4 border-t">
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t">
                                                <UsageRadialShape
                                                    title="Flows"
                                                    current={projects.length}
                                                    limit={getPlanLimits().projects}
                                                />
                                                <UsageRadialShape
                                                    title="Monthly Users"
                                                    current={monthlyViews}
                                                    limit={getPlanLimits().monthlyViews}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </section>
                            </div>
                        </main>
                    </>
                ) : null
                }
            </div >


            {/* Pricing Overlay */}
            {
                showPricing && (
                    <div className="fixed inset-0 z-50 bg-background overflow-auto animate-in fade-in slide-in-from-bottom-10 duration-200">
                        <div className="absolute top-4 right-4">
                            <Button variant="ghost" onClick={() => {
                                setShowPricing(false);
                                setPaywallReason(null);
                            }}>
                                Close
                            </Button>
                        </div>
                        <PricingPage
                            onClose={() => {
                                setShowPricing(false);
                                setPaywallReason(null);
                            }}
                            limitReason={paywallReason}
                        />
                    </div>
                )
            }
            {/* Walkthrough Modal */}
            <WalkthroughModal
                open={showWalkthrough}
                onOpenChange={setShowWalkthrough}
                onComplete={handleWalkthroughComplete}
                onAction={handleWalkthroughAction}
            />
        </div >
    );
};

export default Dashboard;
